import { useState, useEffect } from 'react';

import {
  useAuth,
  useConvert,
  useAuthRefresh,
  useFrozenOAuth,
  useConversionMode,
  useConverterParams,
  useAccountPlaylists,
  useConversionParams,
} from '@/hooks';
import {
  Alert,
  Loader,
  OAuthForm,
  TypeSelect,
  SupportCard,
  ConvertButton,
  DetailsToggle,
  ErrorRenderer,
  HoneypotInput,
  PrivacyToggle,
  PlaylistPicker,
  PrimaryAuthForm,
  ConversionRenderer,
  SourceTargetSelect,
  NewConversionButton,
  ConversionModeSelect,
} from '@/components';
import { isApple, isYoutube } from '@/utils';
import { CONVERSION, CONVERSION_MODE, PLATFORM_CAPTIONS } from '@/constants';

const { account } = CONVERSION;

const { default: defaultMode } = CONVERSION_MODE;

const AccountConverter = () => {
  const { sourceParam, targetParam } = useConversionParams();

  const [type, setType] = useState('');
  const [sourceIds, setSourceIds] = useState([]);
  const [isPrivate, setIsPrivate] = useState(true);
  const [botDetected, setBotDetected] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);

  const { source, target, isActive, setSource, setTarget } = useConverterParams(
    account,
    {
      restoreGuard: !sourceParam,
      source: { routeParam: sourceParam },
      target: { routeParam: targetParam },
    }
  );

  const { isAuthorized, onUnauthorized } = useAuth();

  const {
    isLoading,
    playlists,
    getPlaylists,
    clearPlaylists,
    error: playlistError,
  } = useAccountPlaylists({
    source,
    target,
    onUnauthorized,
  });

  const isSelection = type === CONVERSION.accountSel;

  const {
    mode,
    setMode,
    wouldSkipAll,
    priorConversions,
    hasPriorConversions,
    isLoading: isLoadingPriors,
  } = useConversionMode({
    source,
    target,
    sourceIds,
    isSelection,
    withSourceAccount: true,
  });

  const {
    isFrozen,
    progress,
    conversion,
    isComplete,
    isConverting,
    handleConvert,
    clearConversion,
    error: conversionError,
  } = useConvert({
    mode,
    type,
    source,
    target,
    isPrivate,
    sourceIds,
    onUnauthorized,
  });

  const { sourceAuth, targetAuth } = useFrozenOAuth({
    source,
    target,
    isFrozen,
  });

  const isSamePlatform = source && source === target;

  useAuthRefresh(source, sourceAuth, isAuthorized, isSamePlatform || !isActive);
  useAuthRefresh(target, targetAuth, isAuthorized, isSamePlatform || !isActive);

  const {
    error: sourceAuthError,
    accountId: sourceAccountId,
    isSignedIn: isSourceSignedIn,
    isSigningIn: isSourceSigningIn,
  } = sourceAuth || {};

  const {
    error: targetAuthError,
    accountId: targetAccountId,
    isSignedIn: isTargetSignedIn,
    isSigningIn: isTargetSigningIn,
  } = targetAuth || {};

  const sourceCaption = PLATFORM_CAPTIONS[source];

  const targetCaption = PLATFORM_CAPTIONS[target];

  const clearSettings = () => {
    clearConversion();
    clearPlaylists();
    setSourceIds([]);
    setMode(defaultMode);
    setType(isYoutube(source) ? CONVERSION.accountSel : '');
  };

  useEffect(() => {
    if (!isFrozen) {
      clearSettings();
    }
  }, [sourceAccountId, targetAccountId]);

  useEffect(() => {
    setIsPrivate(true);
  }, [isSelection]);

  useEffect(() => {
    setIsPrivate(true);

    if (isYoutube(source)) {
      setType(CONVERSION.accountSel);
    } else {
      setType('');
    }
  }, [source, target]);

  const handleStartNew = () => {
    setSource(sourceParam);
    setTarget(targetParam);
    clearSettings();
  };

  const error = playlistError || conversionError;

  const oAuthError = sourceAuthError || targetAuthError;

  const showDetailsBody = !isComplete || isDetailsOpen;

  const isSignedIn = isSourceSignedIn && isTargetSignedIn;

  const isSigningIn = isSourceSigningIn && isTargetSigningIn;

  const needsSelection = isSelection && sourceIds.length < 1;

  const isSameAccount = sourceAccountId && sourceAccountId === targetAccountId;

  const isPrivacyToggleDisabled =
    isFrozen || wouldSkipAll || isApple(target);

  const isConvertButtonDisabled =
    isComplete ||
    !isSignedIn ||
    isSigningIn ||
    isConverting ||
    wouldSkipAll ||
    needsSelection;

  return (
    <>
      <HoneypotInput onBotDetected={setBotDetected} />

      {isAuthorized ? (
        <>
          <DetailsToggle
            isComplete={isComplete}
            isConverting={isConverting}
            isDetailsOpen={isDetailsOpen}
            setIsDetailsOpen={setIsDetailsOpen}
          />

          {showDetailsBody && (
            <>
              {!isComplete && !sourceParam && (
                <SourceTargetSelect
                  source={source}
                  target={target}
                  disabled={isConverting}
                  onSourceChange={setSource}
                  onTargetChange={setTarget}
                />
              )}

              {isSamePlatform && (
                <Alert type="warning">
                  ⚠️ Conversion within the same platform!
                </Alert>
              )}

              {source && target && (
                <>
                  <div className="mb-3">
                    <div className="oauth-group">
                      <OAuthForm
                        role="source"
                        {...sourceAuth}
                        platform={source}
                        disabled={isFrozen}
                      />
                    </div>

                    <div className="oauth-group">
                      <OAuthForm
                        role="target"
                        {...targetAuth}
                        platform={target}
                        disabled={isFrozen}
                      />
                    </div>
                  </div>

                  {isSignedIn && !isSameAccount && (
                    <>
                      <div className="mt-3">
                        <TypeSelect
                          value={type}
                          source={source}
                          onChange={setType}
                          options={{
                            all: CONVERSION.account,
                            selection: CONVERSION.accountSel,
                          }}
                          disabled={isFrozen || isLoading}
                        />
                      </div>

                      {isSelection && (
                        <div className="privacy-convert-group mt-3">
                          <PlaylistPicker
                            target={target}
                            isLoading={isLoading}
                            playlists={playlists}
                            isComplete={isComplete}
                            selectedIds={sourceIds}
                            isConverting={isConverting}
                            onGetPlaylists={getPlaylists}
                            disabled={isFrozen || isLoading}
                            onSelectionChange={setSourceIds}
                          />
                        </div>
                      )}

                      {type &&
                        !needsSelection &&
                        (isLoadingPriors ? (
                          <div className="mt-3">
                            <Loader />
                          </div>
                        ) : (
                          <>
                            {hasPriorConversions && (
                              <div className="mt-3">
                                <ConversionModeSelect
                                  value={mode}
                                  onChange={setMode}
                                  disabled={isFrozen}
                                  isComplete={isComplete}
                                  wouldSkipAll={wouldSkipAll}
                                  priorConversions={priorConversions}
                                  sourceIds={isSelection ? sourceIds : []}
                                  total={isSelection ? sourceIds.length : null}
                                />
                              </div>
                            )}

                            <div className="privacy-convert-group mt-3">
                              <PrivacyToggle
                                type={type}
                                isPrivate={isPrivate}
                                onToggle={setIsPrivate}
                                disabled={isPrivacyToggleDisabled}
                              />

                              <ConvertButton
                                onConvert={handleConvert}
                                disabled={isConvertButtonDisabled}
                              />
                            </div>
                          </>
                        ))}
                    </>
                  )}

                  {oAuthError ? (
                    <Alert type="danger">{oAuthError}</Alert>
                  ) : (
                    <>
                      {!isSignedIn && !isSigningIn && !isFrozen && (
                        <Alert type="info">
                          Connect to allow us to transfer{' '}
                          {isSamePlatform ? (
                            <>playlists across your {sourceCaption} accounts.</>
                          ) : (
                            <>
                              your {sourceCaption} playlists to {targetCaption}.
                            </>
                          )}
                        </Alert>
                      )}
                    </>
                  )}

                  {isSameAccount && (
                    <Alert type="danger">
                      The source and target accounts cannot be the same!
                    </Alert>
                  )}
                </>
              )}
            </>
          )}

          {source && target && isFrozen && (
            <ConversionRenderer
              {...conversion}
              source={source}
              target={target}
              progress={progress}
              isConverting={isConverting}
            />
          )}

          {error && <ErrorRenderer error={error} />}

          {isComplete && (
            <>
              <SupportCard />

              <NewConversionButton onStartNew={handleStartNew} />
            </>
          )}
        </>
      ) : (
        <PrimaryAuthForm botDetected={botDetected} />
      )}
    </>
  );
};

export default AccountConverter;
